
import { Redirect, NewRedirect, RedirectStats } from "@/types/redirectTypes";
import { getAuthHeader } from "@/services/authService";

export const getRedirects = async (): Promise<Redirect[]> => {
  const response = await fetch("/api/admin/redirects", {
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Failed to fetch redirects");
  }

  return response.json();
};

export const getRedirectStats = async (): Promise<RedirectStats> => {
  const redirects = await getRedirects();
  const active = redirects.filter(r => r.is_active).length;
  const totalAccesses = redirects.reduce((sum, r) => sum + r.access_count, 0);
  
  // Get top 5 redirects by access count
  const topRedirects = [...redirects]
    .sort((a, b) => b.access_count - a.access_count)
    .slice(0, 5)
    .map(r => ({
      source_path: r.source_path,
      target_path: r.target_path,
      access_count: r.access_count
    }));

  return {
    total: redirects.length,
    active,
    inactive: redirects.length - active,
    totalAccesses,
    topRedirects
  };
};

export const createRedirect = async (redirect: NewRedirect): Promise<Redirect> => {
  const response = await fetch("/api/admin/redirects", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(redirect),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Failed to create redirect");
  }

  return response.json();
};

export const updateRedirect = async (id: string, redirect: Partial<Redirect>): Promise<Redirect> => {
  // Remove non-updatable fields
  const { id: _, created_at, updated_at, created_by, access_count, last_accessed_at, ...updatableFields } = redirect as any;

  const response = await fetch(`/api/admin/redirects/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(updatableFields),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Failed to update redirect");
  }

  return response.json();
};

export const deleteRedirect = async (id: string): Promise<void> => {
  const response = await fetch(`/api/admin/redirects/${id}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || "Failed to delete redirect");
  }
};

export const bulkImportRedirects = async (redirects: NewRedirect[]): Promise<{
  success: number;
  failed: number;
  errors: any[];
}> => {
  const result = { success: 0, failed: 0, errors: [] };
  
  // Process in batches of 100 to avoid timeouts
  const batchSize = 100;
  const batches = [];
  
  for (let i = 0; i < redirects.length; i += batchSize) {
    batches.push(redirects.slice(i, i + batchSize));
  }
  
  for (const batch of batches) {
    try {
      const responses = await Promise.all(
        batch.map(async (redirect) => {
          const response = await fetch("/api/admin/redirects", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeader(),
            },
            body: JSON.stringify(redirect),
          });
          if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            throw new Error(errorBody.error || "Failed to create redirect");
          }
          return response.json();
        }),
      );
      result.success += responses.length;
    } catch (error) {
      result.failed += batch.length;
      result.errors.push(error);
    }
  }
  
  return result;
};

export const exportRedirects = async (): Promise<Redirect[]> => {
  return await getRedirects();
};

// Function to check if a redirect with the same source path already exists
export const checkRedirectExists = async (sourcePath: string, excludeId?: string): Promise<boolean> => {
  const redirects = await getRedirects();
  return redirects.some((redirect) => {
    if (excludeId && redirect.id === excludeId) return false;
    return redirect.source_path === sourcePath;
  });
};
